import { useContext, useEffect, useState, useMemo, memo, useRef } from 'react'

import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import Card from 'atoms/Card'
import { motion } from 'framer-motion'
import ToDoList from '../newdash/components/ToDoList'

import DataTable from './components/DataTable'
import { Uploader } from 'layouts/authenticated/pages/documents/components/Uploader'
import { IntakeContext } from 'context/IntakeContext'
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3' // ES Modules import
import awsConfig from 'aws-exports'
import { Auth } from 'aws-amplify'
import { apiClient } from 'utils/api/apiClient'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { Grid, Box, Button, Typography, IconButton } from '@mui/material'
import { Article, Delete, Description, Download } from '@mui/icons-material'
import GlobalDropzone from './components/Uploader/GlobalDropzone'

export const NewDocuments = memo(() => {
  const [filesToUpload, setFilesToUpload] = useState([])
  const [accessLevel, setAccessLevel] = useState('private')
  const [lastUploaded, setLastUploaded] = useState(Number(new Date()))
  const { assumedUser, loggedInUserId, isAdmin } = useContext(IntakeContext)

  //Quick and dirty method to make sure that documents are
  //loaded when the documents component is rendered
  // this - refreshes every 5 minutes
  // useEffect(() => {
  //   const shouldReload = localStorage.getItem('shouldReload')
  //   if (shouldReload !== 'false') {
  //     // Set the item to prevent further reloads
  //     localStorage.setItem('shouldReload', 'false')
  //     window.location.reload()
  //   }
  //   // Set a timeout to clear the 'shouldReload' flag after 5 minutes
  //   const timeoutId = setTimeout(() => {
  //     localStorage.removeItem('shouldReload')
  //   }, 300000)
  // }, [])

  const downloadFile = async ({ filePath }) => {
    const client = new S3Client({
      region: 'us-east-2',
      credentials: Auth.currentCredentials(),
    })
    const command = new GetObjectCommand({
      Bucket: awsConfig.aws_user_files_s3_bucket,
      Key: filePath,
    })
    return await client.send(command)
  }

  const downloadBlob = (blob, filename) => {
    const adjustedFileName =
      filename.slice(0, filename.lastIndexOf('_')) +
      filename.slice(filename.lastIndexOf('.'))
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = adjustedFileName || 'download'
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url)
        a.removeEventListener('click', clickHandler)
      }, 150)
    }
    a.addEventListener('click', clickHandler, false)
    a.click()
    return a
  }

  const triggerDocumentsRefetch = () => {
    // trigger re-fetching of documents by
    // updating a dependency of the useEffect that fetches them
    setLastUploaded(Number(new Date()))
  }

  const deleteUpload = async ({ fileKey, createdAt }) => {
    let documentsPath = `/documents/upload/${fileKey}/${createdAt}`
    if (isAdmin && assumedUser) {
      documentsPath = `/admin/documents/upload/${fileKey}/${createdAt}/${assumedUser}`
    }
    const { data } = await apiClient.delete(documentsPath)
    triggerDocumentsRefetch()
    toast.success('You have successfully deleted the document', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
    })
  }

  const streamToBlob = async (stream, mimeType) => {
    const reader = stream.getReader()
    const chunks = []

    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        break
      }

      chunks.push(value)
    }

    return new Blob(chunks, { type: mimeType })
  }

  const fetchDocumentFromApi = async ({ fileKey }) => {
    let documentsPath = `/documents/${fileKey}`
    if (isAdmin && assumedUser) {
      documentsPath = `/admin/documents/${fileKey}/${assumedUser}`
    }
    return apiClient.get(documentsPath)
  }

  const getPreview = async ({ fileKey }) => {
    const {
      data: { documentUrl },
    } = await fetchDocumentFromApi({ fileKey })
    window.open(documentUrl, '_blank')
  }

  const download = async ({ fileKey }) => {
    const {
      data: { documentUrl },
    } = await fetchDocumentFromApi({ fileKey })
    const attachmentDownloadResponse = await fetch(documentUrl)
    const blob = await attachmentDownloadResponse.blob()
    downloadBlob(blob, fileKey)
  }

  const uploadedDocuments = useMemo(() => {
    return {
      columns: [
        {
          Header: 'Document Name',
          accessor: 'documentLink',
          Cell: ({ value: [data] }) => {
            const { fileName, link, index } = data
            const isDisabled = ['.doc', '.docx', '.xls', '.xlsx'].includes(
              fileName.slice(fileName.lastIndexOf('.'))
            )
            return (
              <Button
                disabled={isDisabled}
                data-testid={`preview-${index}-${fileName}`}
                sx={[
                  {
                    textTransform: 'none',
                    fontSize: '15px',
                    color: '#495057',
                    fontWeight: 'normal',
                    boxShadow: 'none',
                    padding: '0px',
                  },
                  {
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      color: '#000',
                      boxShadow: 'none',
                    },
                    '>span': {
                      marginBottom: '2px',
                    },
                  },
                ]}
                startIcon={<Description />}
                href={link}
                onClick={(e) => {
                  e.preventDefault()
                  if (isDisabled) return
                  getPreview({ fileKey: fileName })
                }}
              >
                {fileName.slice(0, fileName.lastIndexOf('_')) +
                  fileName.slice(fileName.lastIndexOf('.'))}
              </Button>
            )
          },
        },
        {
          Header: 'upload date',
          accessor: 'uploadDate',
          Cell: ({ value }) => <Typography variant="body2">{value}</Typography>,
        },
        {
          Header: 'actions',
          accessor: 'documentLinkForDelete',
          Cell: ({ value: [data] }) => {
            const { fileName, link, createdAt, index } = data

            return (
              <Box>
                <IconButton
                  sx={{
                    padding: '4px',
                    width: '44px',
                    borderRadius: '20px',
                    color: '#fff',
                    backgroundColor: '#2370ED',
                    background: `linear-gradient(135deg, #2370ED, #143F87)`,
                  }}
                  href={link}
                  onClick={(e) => {
                    e.preventDefault()
                    download({ fileKey: fileName })
                  }}
                >
                  <Download />
                </IconButton>
                <IconButton
                  sx={{
                    padding: '4px',
                    width: '44px',
                    borderRadius: '20px',
                    color: '#fff',
                    backgroundColor: '#FF2F32',
                    background: `linear-gradient(135deg, #FF2F32, #F76A1E)`,
                    marginLeft: '8px',
                  }}
                  href={link}
                  onClick={(e) => {
                    e.preventDefault()
                    deleteUpload({ fileKey: fileName, createdAt })
                  }}
                  data-testid={`delete-${fileName}-${index}`}
                >
                  <Delete />
                </IconButton>
              </Box>
            )
          },
        },
      ],

      rows: [],
    }
  }, [])

  const [files, setFiles] = useState(uploadedDocuments)

  useEffect(() => {
    const getFiles = async () => {
      let documentsPath = '/documents'
      if (isAdmin && assumedUser) {
        documentsPath = `/admin/documents/${assumedUser}`
      }
      const { data, status } = await apiClient(documentsPath)
      if (status !== 200) {
        setFiles({ ...uploadedDocuments, rows: [] })
        return
      }
      const rows =
        data?.map(({ fileName, createdAt, updatedAt }, index) => {
          return {
            documentLinkForDelete: [{ fileName, createdAt, link: '#', index }],
            uploadDate: format(new Date(updatedAt), 'MM/d/yyyy'),
            documentLink: [{ fileName, createdAt, link: '#', index }], // [fileName, { link: '#', filePath }],
          }
        }) || []
      setFiles({ ...uploadedDocuments, rows })
    }
    getFiles()
  }, [uploadedDocuments, accessLevel, lastUploaded])

  const uploaderDropzoneRef = useRef(null)

  return (
    <DashboardLayout>
      <Box
        px={{
          xs: 4,
          sm: 4,
          md: 4,
          lg: 6,
          xl: 6,
          xxl: 6,
          test: 6,
          xl3: 6,
        }}
      >
        <Grid container spacing={{ xs: 1, lg: 6 }}>
          <Grid item xs={12} mb={4} mt={8} display={'flex'}>
            <Typography
              fontWeight="400"
              fontSize="28px"
              textAlign="left"
              color="#262627"
            >
              <Box sx={{ display: 'inline-block', fontWeight: 600 }}>
                Documents
              </Box>{' '}
              - Upload anything you would like PARCO to see:
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={{ xs: 3, lg: 6 }}
          sx={{
            flexDirection: {
              xs: 'column-reverse', // default
              lg: 'row', // reverse at lg and up
            },
          }}
        >
          <Grid item xs={12} lg={6}>
            <Grid container spacing={{ xs: 3, lg: 6 }}>
              <Grid item xs={12}>
                <GlobalDropzone
                  onFilesDropped={(files) => {
                    // console.log('Dropped files:', files)
                    // Send dropped files to Uploader's Dropzone instance
                    if (uploaderDropzoneRef.current) {
                      uploaderDropzoneRef.current.addFiles(files)
                    }
                  }}
                >
                  <motion.div
                    // style={{ minWidth: '500px' }}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <Typography
                      display="flex"
                      align="left"
                      variant="body2FunHeader"
                      mb={2}
                    >
                      DOCUMENT UPLOAD
                    </Typography>
                    <Card>
                      <Box p={3} lineHeight={1}>
                        <Box display="flex">
                          <Box textAlign="left">
                            <Typography variant="body1" fontWeight="light">
                              Upload any relevant financial documents below:
                            </Typography>
                          </Box>
                        </Box>

                        <Uploader
                          ref={uploaderDropzoneRef}
                          prefix={assumedUser || loggedInUserId}
                          cacheBuster={() =>
                            setLastUploaded(Number(new Date()))
                          }
                        />
                      </Box>
                    </Card>
                  </motion.div>
                </GlobalDropzone>
              </Grid>
              <Grid item xs={12}>
                {/* TO DO LIST */}
                <ToDoList
                  items={[
                    { header: 'Upload a recent Leave & Earnings Statement' },
                    { header: 'Upload a recent TSP Statement' },
                    { header: 'Upload a Social Security Benefits Statement' },
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* UPLOADED FILES */}
          <Grid item xs={12} lg={6}>
            <motion.div
              // style={{ minWidth: '500px' }}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <Typography
                display="flex"
                align="left"
                variant="body2FunHeader"
                mb={2}
              >
                UPLOADED DOCUMENTS
              </Typography>
              <Card>
                <DataTable
                  table={files}
                  canSearch
                  entriesPerPage={{ defaultValue: 20 }}
                  alternateTableHeader=""
                  alternateTableSubHeader=""
                  clearHeader
                  tableRowStyleAdjustment={{
                    td: {
                      maxWidth: '300px',
                      overflowX: 'clip',
                    },
                  }}
                />
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  )
})
NewDocuments.displayName = 'NewDocuments'
